import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gamepad2, Plus, ArrowLeft, Loader2, Save, Sparkles, ImageIcon, Users, Package, Trophy, Play, Star, MessageCircle, Send, Activity, SlidersHorizontal, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { VaultChat, isQuestionBatch } from "./VaultChat";
import { MentionInput } from "./MentionInput";
import { MoveToVaultButton } from "./MoveToVault";
import { parseMentions } from "../lib/mentions";

const STARTERS = [
  "We're 8 adults at a reunion, want something competitive",
  "Design a digital game we can play over video call",
  "A calm game for grandparents and little kids together",
];

export function GameGenerator() {
  const [view, setView] = useState("home"); // home | saved | generate | read
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveText, setSaveText] = useState("");
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ minRating: 0, intensity: null, supplies: [] });

  useEffect(() => { api.get("/families/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  const loadGames = async () => {
    setLoading(true);
    try { const { data } = await api.get("/vault/games"); setGames(data); }
    catch { setGames([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (view === "saved") loadGames(); }, [view]);

  if (view === "home") {
    return (
      <div className="pt-2" data-testid="game-home">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="h-16 w-16 rounded-2xl grid place-items-center mb-3" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
            <Gamepad2 size={30} className="gold-text" />
          </div>
          <div className="font-serif-display text-3xl">Game Generator</div>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Dream up full family games — then save them for everyone.</p>
        </div>
        <div className="space-y-3">
          <motion.button whileTap={{ scale: 0.98 }} onClick={() => setView("generate")} data-testid="game-generate-new"
            className="w-full rounded-2xl p-4 flex items-center gap-3 text-left" style={{ background: "var(--accent)", color: "#fff" }}>
            <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "rgba(255,255,255,0.18)" }}><Plus size={22} /></div>
            <div><div className="font-semibold">Generate New</div><div className="text-xs opacity-80">Answer a few questions, get a full game</div></div>
          </motion.button>
          <motion.button whileTap={{ scale: 0.98 }} onClick={() => setView("saved")} data-testid="game-saved"
            className="w-full card-surface rounded-2xl p-4 flex items-center gap-3 text-left">
            <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}><Sparkles size={22} className="gold-text" /></div>
            <div><div className="font-semibold" style={{ color: "var(--text-primary)" }}>Saved Games</div><div className="text-xs" style={{ color: "var(--text-secondary)" }}>Games your family has kept</div></div>
          </motion.button>
        </div>
      </div>
    );
  }

  if (view === "saved") {
    const filtered = games.filter((g) => {
      if (filters.minRating && (g.avg_rating || 0) < filters.minRating) return false;
      if (filters.intensity && (g.intensity || "medium") !== filters.intensity) return false;
      if (filters.supplies.length) {
        const tags = g.supplies_tags || [];
        if (!filters.supplies.some((s) => tags.includes(s))) return false;
      }
      return true;
    });
    return (
      <div data-testid="game-saved-list">
        <button onClick={() => setView("home")} className="flex items-center gap-1.5 text-sm mb-3" style={{ color: "var(--text-secondary)" }} data-testid="game-back">
          <ArrowLeft size={16} /> Back
        </button>
        {loading ? (
          <div className="grid place-items-center py-12"><Loader2 className="animate-spin gold-text" /></div>
        ) : games.length === 0 ? (
          <div className="text-center py-12">
            <Gamepad2 size={30} className="gold-text mx-auto mb-2" />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>No saved games yet. Generate one and save it for the family.</p>
          </div>
        ) : (
          <>
            <GameFilters filters={filters} setFilters={setFilters} />
            {filtered.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: "var(--text-muted)" }} data-testid="game-filter-empty">No games match these filters.</p>
            ) : (
              <div className="space-y-3">
                {filtered.map((g) => (
                  <motion.button key={g.id} whileTap={{ scale: 0.98 }} onClick={() => { setActive(g); setPlaying(false); setView("read"); }}
                    data-testid={`game-card-${g.id}`} className="w-full card-surface rounded-2xl overflow-hidden text-left flex items-center gap-3">
                    <div className="h-24 w-24 shrink-0 grid place-items-center" style={{ background: "var(--overlay)" }}>
                      {g.image ? <img src={fileUrl(g.image)} alt="" className="h-full w-full object-cover" /> : <Gamepad2 size={26} className="gold-text" />}
                    </div>
                    <div className="py-2 pr-3 min-w-0 flex-1">
                      <div className="font-serif-display text-lg leading-tight truncate">{g.name}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Stars value={g.avg_rating || 0} size={13} />
                        <span className="text-xs" style={{ color: "var(--text-muted)" }}>{g.avg_rating ? `${g.avg_rating} (${g.rating_count})` : "Not rated"}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <IntensityBadge level={g.intensity} />
                        {(g.supplies_tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>{t}</span>
                        ))}
                        <span className="flex items-center gap-1 text-xs" style={{ color: "var(--text-muted)" }}><MessageCircle size={12} /> {g.comment_count || 0}</span>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (view === "read" && active) {
    return <GameRead game={active} members={members} playing={playing} setPlaying={setPlaying}
      onBack={() => { setView("saved"); setPlaying(false); }} />;
  }

  // generate
  return (
    <div className="flex flex-col h-full" data-testid="game-generate">
      <button onClick={() => setView("home")} className="flex items-center gap-1.5 text-sm mb-1 shrink-0" style={{ color: "var(--text-secondary)" }} data-testid="game-gen-back">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="flex-1 min-h-0">
        <VaultChat
          tool="game_generator"
          testid="game-chat"
          greeting="Let's build a game! Tell me who's playing and where — a hangout, a reunion, or over video — and I'll ask a few quick questions to design the perfect one."
          starters={STARTERS}
          placeholder="Describe what you'd like…"
          footer={(lastText) => {
            const canSave = lastText && !isQuestionBatch(lastText) && lastText.length > 120;
            return (
              <button onClick={() => { setSaveText(lastText); setSaveOpen(true); }} disabled={!canSave} data-testid="game-save-btn"
                className="w-full rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 mt-2"
                style={{ background: canSave ? "color-mix(in srgb, var(--accent) 14%, transparent)" : "var(--overlay)", border: "1px solid var(--accent)", color: "var(--accent)", opacity: canSave ? 1 : 0.5 }}>
                <Save size={15} /> Save this game for the family
              </button>
            );
          }}
        />
      </div>
      {saveOpen && <SaveGameModal content={saveText} onClose={() => setSaveOpen(false)} onSaved={() => { setSaveOpen(false); setView("saved"); }} />}
    </div>
  );
}

function GameRead({ game, members, playing, setPlaying, onBack }) {
  const [rating, setRating] = useState({ avg: game.avg_rating || 0, count: game.rating_count || 0, mine: game.my_rating || 0 });
  const [hover, setHover] = useState(0);
  const [comments, setComments] = useState([]);
  const [loadingC, setLoadingC] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [customized, setCustomized] = useState(null);
  const p = game.profile || null;

  const customize = async () => {
    setCustomizing(true);
    try {
      const { data } = await api.post(`/vault/games/${game.id}/customize`);
      setCustomized(data.content);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCustomizing(false); }
  };

  useEffect(() => {
    (async () => {
      setLoadingC(true);
      try { const { data } = await api.get(`/vault/games/${game.id}/comments`); setComments(data); }
      catch { setComments([]); }
      finally { setLoadingC(false); }
    })();
  }, [game.id]);

  const rate = async (stars) => {
    setRating((r) => ({ ...r, mine: stars }));
    try { const { data } = await api.post(`/vault/games/${game.id}/rate`, { stars }); setRating({ avg: data.avg_rating, count: data.rating_count, mine: data.my_rating }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/vault/games/${game.id}/comments`, { content: text.trim(), mentions: parseMentions(text, members) });
      setComments((c) => [...c, data]); setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="game-read">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm mb-3" style={{ color: "var(--text-secondary)" }} data-testid="game-read-back">
        <ArrowLeft size={16} /> Saved games
      </button>
      {game.image && <img src={fileUrl(game.image)} alt="" className="w-full h-44 object-cover rounded-2xl mb-3" />}
      <div className="font-serif-display text-3xl mb-1">{game.name}</div>
      <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>Saved by {game.author?.name || "Family"}</div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <IntensityBadge level={game.intensity} />
        {(game.supplies_tags || []).map((t) => (
          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}><Package size={10} /> {t}</span>
        ))}
      </div>

      {playing ? (
        <>
          <button onClick={() => setPlaying(false)} className="flex items-center gap-1.5 text-xs gold-text mb-3" data-testid="game-back-overview">
            <ArrowLeft size={14} /> Back to overview
          </button>
          <div className="vault-md" data-testid="game-play-detail">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{(p && p.how_to_play) || game.content}</ReactMarkdown>
          </div>
        </>
      ) : (
        <>
          {p ? (
            <div className="space-y-3">
              {p.overview && <p className="text-sm" style={{ color: "var(--text-primary)" }} data-testid="game-overview">{p.overview}</p>}
              <ProfileRow icon={Users} label="Best for" value={p.best_for} />
              <ProfileRow icon={Package} label="Supplies" value={p.supplies} />
              <ProfileRow icon={Trophy} label="How to win" value={p.how_to_win} />
            </div>
          ) : (
            <p className="text-sm" style={{ color: "var(--text-primary)" }} data-testid="game-overview">
              {(game.content || "").replace(/[#*_`>]/g, "").trim().split("\n").filter(Boolean).slice(0, 3).join(" ").slice(0, 260) || "A saved family game."}
            </p>
          )}
          <button onClick={() => setPlaying(true)} data-testid="game-play-btn"
            className="w-full rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2 mt-5"
            style={{ background: "var(--accent)", color: "#fff" }}>
            <Play size={16} /> Play Game
          </button>
          <button onClick={customize} disabled={customizing} data-testid="game-customize-btn"
            className="w-full rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2 mt-2"
            style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)", border: "1px solid var(--accent)", color: "var(--accent)", opacity: customizing ? 0.6 : 1 }}>
            {customizing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Customize for me
          </button>
          <p className="text-[11px] text-center mt-1.5" style={{ color: "var(--text-muted)" }}>Generates fresh specifics for this family — the saved game keeps only the reusable rules.</p>

          <MoveToVaultButton
            testid="game-move-to-vault"
            className="w-full rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2 mt-2"
            media={{ path: game.image || null, kind: "image", title: game.name, source: "game", ref_id: game.id, ref_collection: "games", body_md: game.content || (game.profile && game.profile.how_to_play) || null }}
          />

          {/* Rating */}
          <div className="rounded-2xl p-4 mt-5 text-center" style={{ background: "var(--overlay)" }} data-testid="game-rating">
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>
              {rating.mine ? "Your rating" : "Rate this game"}
            </div>
            <div className="flex items-center justify-center gap-1.5" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} onClick={() => rate(s)} onMouseEnter={() => setHover(s)} data-testid={`game-rate-${s}`} className="p-0.5">
                  <Star size={28} className={(hover || rating.mine) >= s ? "fill-current" : ""} style={{ color: (hover || rating.mine) >= s ? "var(--accent)" : "var(--text-muted)" }} />
                </button>
              ))}
            </div>
            <div className="text-xs mt-2" style={{ color: "var(--text-secondary)" }} data-testid="game-rating-summary">
              {rating.count ? `${rating.avg} average · ${rating.count} rating${rating.count === 1 ? "" : "s"}` : "Be the first to rate"}
            </div>
          </div>

          {/* Comments */}
          <div className="flex items-center gap-1.5 mt-5 mb-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            <MessageCircle size={16} /> {comments.length} comment{comments.length === 1 ? "" : "s"}
          </div>
          <div className="space-y-2.5" data-testid="game-comments">
            {loadingC ? <Loader2 className="animate-spin gold-text" size={18} /> : comments.map((c) => (
              <div key={c.id} className="rounded-xl p-3" style={{ background: "var(--overlay)" }} data-testid={`game-comment-${c.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)", color: "var(--accent)" }}>
                    {(c.author?.name || "?").charAt(0)}
                  </div>
                  <span className="text-xs font-semibold">{c.author?.name}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{c.content}</p>
              </div>
            ))}
            {!loadingC && comments.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>No comments yet. Start the conversation.</p>}
          </div>
          <div className="flex items-end gap-2 mt-3">
            <MentionInput value={text} onChange={setText} members={members} onEnter={send} wrapperClassName="flex-1"
              placeholder="Add a comment… use @ to tag" testid="game-comment-input"
              className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
            <button onClick={send} disabled={sending} data-testid="game-comment-send" className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            </button>
          </div>
        </>
      )}
      {customized !== null && (
        <div className="fixed inset-0 z-[95] flex flex-col" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={() => setCustomized(null)} data-testid="game-customized-modal">
          <div className="mt-auto rounded-t-3xl max-h-[90dvh] flex flex-col" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 shrink-0">
              <span className="font-serif-display text-2xl">Customized for your family</span>
              <button onClick={() => setCustomized(null)} data-testid="game-customized-close" className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "var(--overlay)" }}><X size={18} /></button>
            </div>
            <div className="px-5 pb-8 overflow-y-auto no-scrollbar">
              <div className="vault-md" data-testid="game-customized-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{customized}</ReactMarkdown></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stars({ value, size = 14 }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={size} className={value >= s ? "fill-current" : ""} style={{ color: value >= s ? "var(--accent)" : "var(--text-muted)" }} />
      ))}
    </span>
  );
}

const INTENSITY = { high: ["High", "#E0563E"], medium: ["Medium", "#D9A441"], low: ["Low", "#5E9E7E"] };
function IntensityBadge({ level }) {
  const [label, color] = INTENSITY[level] || INTENSITY.medium;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1" style={{ background: `color-mix(in srgb, ${color} 18%, transparent)`, color }} data-testid={`game-intensity-${level || "medium"}`}>
      <Activity size={10} /> {label}
    </span>
  );
}

const SUPPLY_FILTER = ["None", "Cards", "Rope", "Flags", "Pen & Paper", "Other"];
function GameFilters({ filters, setFilters }) {
  const toggleSupply = (s) => setFilters((f) => ({ ...f, supplies: f.supplies.includes(s) ? f.supplies.filter((x) => x !== s) : [...f.supplies, s] }));
  const active = filters.minRating || filters.intensity || filters.supplies.length;
  return (
    <div className="rounded-2xl p-3 mb-4" style={{ background: "var(--overlay)" }} data-testid="game-filters">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--text-secondary)" }}><SlidersHorizontal size={13} /> Filter games</div>
        {active ? <button onClick={() => setFilters({ minRating: 0, intensity: null, supplies: [] })} data-testid="game-filter-clear" className="text-[11px] gold-text">Clear</button> : null}
      </div>
      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>Minimum rating</div>
      <div className="flex gap-1.5 mb-2.5 flex-wrap">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} onClick={() => setFilters((f) => ({ ...f, minRating: f.minRating === s ? 0 : s }))} data-testid={`game-filter-rating-${s}`}
            className="flex items-center gap-0.5 px-2 py-1 rounded-full text-xs"
            style={filters.minRating === s ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg-surface)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
            {s}<Star size={11} className="fill-current" />+
          </button>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>Physical intensity</div>
      <div className="flex gap-1.5 mb-2.5">
        {["high", "medium", "low"].map((lv) => (
          <button key={lv} onClick={() => setFilters((f) => ({ ...f, intensity: f.intensity === lv ? null : lv }))} data-testid={`game-filter-intensity-${lv}`}
            className="px-2.5 py-1 rounded-full text-xs capitalize"
            style={filters.intensity === lv ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg-surface)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
            {lv}
          </button>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>Supplies needed</div>
      <div className="flex gap-1.5 flex-wrap">
        {SUPPLY_FILTER.map((s) => (
          <button key={s} onClick={() => toggleSupply(s)} data-testid={`game-filter-supply-${s.replace(/\s|&/g, "-")}`}
            className="px-2.5 py-1 rounded-full text-xs"
            style={filters.supplies.includes(s) ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg-surface)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ProfileRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 rounded-xl p-3" style={{ background: "var(--overlay)" }} data-testid={`game-profile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
        <Icon size={16} className="gold-text" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="text-sm" style={{ color: "var(--text-primary)" }}>{value}</div>
      </div>
    </div>
  );
}

function SaveGameModal({ content, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState("");

  const save = async () => {
    if (!name.trim()) { toast.error("Give your game a name"); return; }
    setSaving(true);
    let image = null;
    try {
      setStage("Creating cover art…");
      try {
        const { data } = await api.post("/vault/games/cover", { name: name.trim(), description: content.slice(0, 500) });
        image = data.path;
      } catch { /* cover optional */ }
      setStage("Saving & writing the game guide…");
      await api.post("/vault/games", { name: name.trim(), image, content });
      toast.success("Game saved for the family");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); setStage(""); }
  };

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center px-6" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="game-save-modal">
      <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl p-5" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
        <div className="font-serif-display text-2xl mb-1">Save game</div>
        <p className="text-xs mb-4" style={{ color: "var(--text-secondary)" }}>We'll generate a cover image and share it with everyone in your family.</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Game name" data-testid="game-save-name"
          className="w-full rounded-xl px-4 py-3 text-sm outline-none mb-4" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
        {saving ? (
          <div className="flex items-center justify-center gap-2 py-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            <Loader2 size={16} className="animate-spin gold-text" /> {stage || "Working…"}
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 rounded-xl py-2.5 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }} data-testid="game-save-cancel">Cancel</button>
            <button onClick={save} data-testid="game-save-confirm"
              className="flex-1 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5" style={{ background: "var(--accent)", color: "#fff" }}>
              <ImageIcon size={15} /> Save
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
